// assets/product-custom-property.js
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} [textInput] - The text input.
 * @property {HTMLElement} [characterCount] - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Set<string>} */
  #matchingVariantIds = new Set();

  /** @type {string[]} */
  #keywords = [];

  connectedCallback() {
    super.connectedCallback();

    const matchingIdsAttr = this.dataset.matchingVariantIds;
    if (matchingIdsAttr === undefined) return;

    this.#matchingVariantIds = new Set(
      matchingIdsAttr
        .split(',')
        .map((id) => this.#normalizeVariantId(id))
        .filter(Boolean)
    );

    this.#keywords = (this.dataset.variantTitleKeywords || '')
      .split(',')
      .map((keyword) => keyword.trim().toLowerCase())
      .filter(Boolean);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Prefer option values immediately — they form the Shopify variant title.
    const selectedOptions = /** @type {{ name?: string, value?: string }[] | undefined} */ (
      /** @type {any} */ (event).selectedOptions
    );
    let appliedFromTitle = false;
    if (this.#keywords.length > 0 && Array.isArray(selectedOptions) && selectedOptions.length > 0) {
      const titleFromOptions = selectedOptions
        .map((option) => option?.value)
        .filter(Boolean)
        .join(' / ');
      if (titleFromOptions) {
        this.#setActive(this.#titleMatchesKeywords(titleFromOptions));
        appliedFromTitle = true;
      }
    }

    const syncVariantId = event.detail?.variantId;
    if (!appliedFromTitle && typeof syncVariantId === 'string' && syncVariantId !== '') {
      this.#setActive(this.#idMatches(syncVariantId));
    }

    event.promise
      .then((result) => {
        const detail = result?.detail;
        if (detail?.productId && this.dataset.productId && String(detail.productId) !== String(this.dataset.productId)) {
          return;
        }

        const variant = result?.variant;
        const resource = detail?.resource;
        const title = variant?.title || resource?.title;
        if (title) {
          this.#setActive(this.#titleMatchesKeywords(title));
          return;
        }

        const resourceId = resource?.id;
        const resolvedId =
          (typeof resourceId === 'number' || typeof resourceId === 'string' ? String(resourceId) : null) ||
          (variant?.id != null ? String(variant.id) : null) ||
          this.#getFormVariantId();

        if (resolvedId) {
          this.#setActive(this.#idMatches(resolvedId));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[product-custom-property] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {string} title
   * @returns {boolean}
   */
  #titleMatchesKeywords(title) {
    if (this.#keywords.length === 0) return true;
    const lower = title.toLowerCase();
    return this.#keywords.some((keyword) => lower.includes(keyword));
  }

  /**
   * @param {string | number} variantId
   * @returns {boolean}
   */
  #idMatches(variantId) {
    return this.#matchingVariantIds.has(this.#normalizeVariantId(variantId));
  }

  /**
   * Normalize numeric IDs and ProductVariant GIDs to a bare numeric string.
   * @param {string | number} variantId
   * @returns {string}
   */
  #normalizeVariantId(variantId) {
    const value = String(variantId).trim();
    if (!value) return '';
    const gidMatch = value.match(/ProductVariant\/(\d+)/);
    return gidMatch ? gidMatch[1] : value;
  }

  /**
   * @param {boolean} isActive
   */
  #setActive(isActive) {
    this.hidden = !isActive;
    this.toggleAttribute('hidden', !isActive);

    const isRequired = this.dataset.isRequired === 'true';

    for (const input of this.#getPropertyInputs()) {
      if (isActive) {
        input.disabled = false;
        if (isRequired) {
          input.required = true;
          input.setAttribute('aria-required', 'true');
        }
      } else {
        input.disabled = true;
        input.required = false;
        input.removeAttribute('aria-required');
        if (input instanceof HTMLInputElement && input.type === 'checkbox') {
          input.checked = false;
        } else {
          input.value = '';
        }
      }
    }

    if (isActive) {
      this.#updateCharacterCount();
    } else {
      this.#resetCharacterCount();
    }
  }

  /** @returns {Array<HTMLInputElement | HTMLTextAreaElement>} */
  #getPropertyInputs() {
    return Array.from(this.querySelectorAll('input, textarea')).filter(
      (el) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
    );
  }

  /** @returns {string | null} */
  #getFormVariantId() {
    const formId = this.querySelector('input[form], textarea[form]')?.getAttribute('form');
    if (!formId) return null;
    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return null;
    const variantInput = form.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return null;
    return variantInput.value;
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  #resetCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    characterCount.textContent = template
      .replace('[current]', '0')
      .replace('[max]', textInput.maxLength.toString());
  }
}

if (!customElements.get('product-custom-property-component')) {
  customElements.define('product-custom-property-component', ProductCustomProperty);
}
